import { useSyncExternalStore, useCallback } from 'react'

/**
 * Pinned navigation destinations (by href), persisted in localStorage and shared
 * across the sidebar and command palette via an external store. Frontend-only for
 * now; server-side per-user sync is a possible future enhancement.
 */
const KEY = 'nav-favorites'
const EMPTY: string[] = []
const listeners = new Set<() => void>()
let cache: string[] | null = null

function read(): string[] {
  if (cache === null) {
    try {
      const raw = localStorage.getItem(KEY)
      const parsed = raw ? JSON.parse(raw) : []
      cache = Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : []
    } catch {
      cache = []
    }
  }
  return cache
}

function write(next: string[]) {
  cache = next
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    /* ignore quota/availability errors */
  }
  listeners.forEach((l) => l())
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) {
      cache = null
      listeners.forEach((l) => l())
    }
  })
}

export function useFavorites() {
  const favorites = useSyncExternalStore(subscribe, read, () => EMPTY)

  const isFavorite = useCallback((href: string) => favorites.includes(href), [favorites])

  const toggleFavorite = useCallback((href: string) => {
    const current = read()
    write(current.includes(href) ? current.filter((h) => h !== href) : [...current, href])
  }, [])

  return { favorites, isFavorite, toggleFavorite }
}
